import { z } from 'zod'

/**
 * Authentication Validation Schemas
 * Using Zod for form validation with Arabic error messages
 */

// ==================== Login Schema ====================

export const loginSchema = z.object({
  username: z
    .string()
    .min(1, { message: 'اسم المستخدم مطلوب' })
    .min(3, { message: 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل' })
    .max(50, { message: 'اسم المستخدم يجب أن لا يتجاوز 50 حرف' }),

  password: z
    .string()
    .min(1, { message: 'كلمة المرور مطلوبة' })
    .min(6, { message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' }),

  remember: z.boolean().optional().default(false),
})

// ==================== Profile Update Schema ====================

export const profileUpdateSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'الاسم مطلوب' })
    .min(3, { message: 'الاسم يجب أن يكون 3 أحرف على الأقل' })
    .max(100, { message: 'الاسم يجب أن لا يتجاوز 100 حرف' }),

  email: z
    .string()
    .min(1, { message: 'البريد الإلكتروني مطلوب' })
    .email({ message: 'البريد الإلكتروني غير صحيح' }),

  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^01[0-2,5]{1}[0-9]{8}$/.test(val),
      { message: 'رقم الهاتف غير صحيح (مثال: 01012345678)' }
    ),
})

// ==================== Change Password Schema ====================

export const changePasswordSchema = z
  .object({
    current_password: z
      .string()
      .min(1, { message: 'كلمة المرور الحالية مطلوبة' }),

    new_password: z
      .string()
      .min(1, { message: 'كلمة المرور الجديدة مطلوبة' })
      .min(8, { message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' })
      .regex(/[A-Z]/, { message: 'كلمة المرور يجب أن تحتوي على حرف كبير' })
      .regex(/[a-z]/, { message: 'كلمة المرور يجب أن تحتوي على حرف صغير' })
      .regex(/[0-9]/, { message: 'كلمة المرور يجب أن تحتوي على رقم' }),

    new_password_confirmation: z
      .string()
      .min(1, { message: 'تأكيد كلمة المرور مطلوب' }),
  })
  .refine((data) => data.new_password === data.new_password_confirmation, {
    message: 'كلمة المرور غير متطابقة',
    path: ['new_password_confirmation'],
  })

// ==================== Register Schema (Future Use) ====================

export const registerSchema = z
  .object({
    username: z
      .string()
      .min(1, { message: 'اسم المستخدم مطلوب' })
      .min(3, { message: 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل' })
      .max(50, { message: 'اسم المستخدم يجب أن لا يتجاوز 50 حرف' })
      .regex(/^[a-zA-Z0-9_]+$/, {
        message: 'اسم المستخدم يجب أن يحتوي على حروف وأرقام فقط'
      }),

    name: z
      .string()
      .min(1, { message: 'الاسم مطلوب' })
      .min(3, { message: 'الاسم يجب أن يكون 3 أحرف على الأقل' })
      .max(100, { message: 'الاسم يجب أن لا يتجاوز 100 حرف' }),

    email: z
      .string()
      .min(1, { message: 'البريد الإلكتروني مطلوب' })
      .email({ message: 'البريد الإلكتروني غير صحيح' }),

    phone: z
      .string()
      .min(1, { message: 'رقم الهاتف مطلوب' })
      .regex(/^01[0-2,5]{1}[0-9]{8}$/, {
        message: 'رقم الهاتف غير صحيح (مثال: 01012345678)'
      }),

    password: z
      .string()
      .min(1, { message: 'كلمة المرور مطلوبة' })
      .min(8, { message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' })
      .regex(/[A-Z]/, { message: 'كلمة المرور يجب أن تحتوي على حرف كبير' })
      .regex(/[a-z]/, { message: 'كلمة المرور يجب أن تحتوي على حرف صغير' })
      .regex(/[0-9]/, { message: 'كلمة المرور يجب أن تحتوي على رقم' }),

    password_confirmation: z
      .string()
      .min(1, { message: 'تأكيد كلمة المرور مطلوب' }),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'كلمة المرور غير متطابقة',
    path: ['password_confirmation'],
  })

// ==================== Forgot Password Schema (Future Use) ====================

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'البريد الإلكتروني مطلوب' })
    .email({ message: 'البريد الإلكتروني غير صحيح' }),
})

// ==================== Reset Password Schema (Future Use) ====================

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, { message: 'رمز إعادة التعيين مطلوب' }),

    email: z
      .string()
      .min(1, { message: 'البريد الإلكتروني مطلوب' })
      .email({ message: 'البريد الإلكتروني غير صحيح' }),

    password: z
      .string()
      .min(1, { message: 'كلمة المرور مطلوبة' })
      .min(8, { message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' })
      .regex(/[A-Z]/, { message: 'كلمة المرور يجب أن تحتوي على حرف كبير' })
      .regex(/[a-z]/, { message: 'كلمة المرور يجب أن تحتوي على حرف صغير' })
      .regex(/[0-9]/, { message: 'كلمة المرور يجب أن تحتوي على رقم' }),

    password_confirmation: z
      .string()
      .min(1, { message: 'تأكيد كلمة المرور مطلوب' }),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'كلمة المرور غير متطابقة',
    path: ['password_confirmation'],
  })

// Export all schemas
export default {
  loginSchema,
  profileUpdateSchema,
  changePasswordSchema,
  registerSchema,
  forgotPasswordSchema,
  resetPasswordSchema,
}
